import React from 'react';
import PropTypes from 'prop-types';
import Price from '../../Price';
import CardZoom from '../shared/CardZoom';
import RemoveButton from '../shared/RemoveButton';
import '../../shared/Card.css';
import './PatchCard.css';

/**
 * PatchCard — Configurator
 *
 * Catalogue card for a single patch: square thumbnail, zoom affordance,
 * optional quantity badge and a title / price row.
 *
 * Figma component set: 4184:19937
 * Variants: State = Default (4184:19935) | Hover (4184:19936)
 *           Details / Title / Price / Quantity — boolean toggles
 *
 * The Figma booleans map to data: the details row renders when `title` or
 * `price` is set, the badge when `quantity` > 0. Hover is CSS-only.
 */
const PatchCard = ({
  image,
  imageAlt = '',
  title,
  price,
  originalPrice,
  quantity = 0,
  onClick,
  onZoom,
  zoomLabel = 'Zoom',
  onRemove,
  removeLabel = 'Remove',
  size = 'default',
  thumbBackground,
  className = '',
}) => {
  const hasDetails = Boolean(title || price);

  const classes = [
    'ds-card',
    'cfg-patch-card',
    hasDetails && 'cfg-patch-card--with-details',
    onClick && 'ds-card--interactive',
    onRemove && 'cfg-patch-card--removable',
    size === 'large' && 'cfg-patch-card--large',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      {onClick && (
        <button
          type="button"
          className="ds-card__hit"
          onClick={onClick}
          aria-label={title || imageAlt || undefined}
        />
      )}

      {quantity > 0 && (
        <span className="cfg-patch-card__qty typo-label">{quantity}</span>
      )}

      <CardZoom onZoom={onZoom} label={zoomLabel} className="cfg-patch-card__zoom" />

      {onRemove && (
        <RemoveButton onClick={onRemove} label={removeLabel} variant="text" className="cfg-patch-card__remove" />
      )}

      <div className="cfg-patch-card__thumb" style={thumbBackground ? { '--cfg-patch-card-thumb-bg': thumbBackground } : undefined}>
        {image && <img className="cfg-patch-card__img" src={image} alt={imageAlt} />}
      </div>

      {hasDetails && (
        <div className="cfg-patch-card__details">
          {title && <span className="cfg-patch-card__title typo-body-sm">{title}</span>}
          {price && (
            <Price
              type="small"
              price={price}
              originalPrice={originalPrice}
              state={originalPrice ? 'discounted' : 'default'}
              className="cfg-patch-card__price"
            />
          )}
        </div>
      )}
    </div>
  );
};

PatchCard.propTypes = {
  /** Patch image URL (rendered object-contain on the gray thumbnail) */
  image: PropTypes.string,
  /** Alt text for the patch image */
  imageAlt: PropTypes.string,
  /** Patch name (typo-body-sm) */
  title: PropTypes.string,
  /** Formatted price, e.g. "49 kr" — rendered through the DS Price */
  price: PropTypes.string,
  /** Formatted strike-through price, e.g. "79 kr" — the Price turns discounted */
  originalPrice: PropTypes.string,
  /** Quantity already added — the top-left badge shows when > 0 */
  quantity: PropTypes.number,
  /** Card click handler — makes the whole card interactive */
  onClick: PropTypes.func,
  /** Zoom click handler — turns the zoom icon into its own button */
  onZoom: PropTypes.func,
  /** Accessible name of the zoom button */
  zoomLabel: PropTypes.string,
  /** Remove handler — an X on the top-right corner (the zoom icon moves to the top-left) */
  onRemove: PropTypes.func,
  /** Accessible name of the remove button */
  removeLabel: PropTypes.string,
  /** 'default' catalogue card, or 'large' — roomier padding and gap for a design / preview card */
  size: PropTypes.oneOf(['default', 'large']),
  /** CSS background of the thumbnail well (e.g. a render on the stage backdrop) */
  thumbBackground: PropTypes.string,
  /** Additional CSS class */
  className: PropTypes.string,
};

export default PatchCard;
